import type { Order, OrderItem, OrderItemState, OrderState, Quote, ResultColour, SampleState } from "@probetalab/contracts";

type QuoteState = Quote["state"];
import type { ChipTone } from "../components/ui.js";

/** Screen vocabulary for order lifecycle states (meeting 2026-10-31: the chips the receptionist reads). */
export const ORDER_STATE_LABEL: Record<OrderState, string> = {
  registered: "Registrada",
  collected: "Muestra tomada",
  in_process: "En proceso",
  reported_partial: "Validado parcial",
  reported_final: "Validado",
  closed: "Entregado",
  cancelled: "Cancelada",
};

export const ITEM_STATE_LABEL: Record<OrderItemState, string> = {
  pending: "Pendiente",
  captured: "Por validar",
  validated: "Validado",
  issued: "Emitido",
  amended: "Corregido",
  cancelled: "Cancelado",
};

export const SAMPLE_STATE_LABEL: Record<SampleState, string> = {
  pending: "Pendiente",
  collected: "Tomada",
  received: "Recibida",
  processed: "Procesada",
  rejected: "Rechazada",
};

export const QUOTE_STATE_LABEL: Record<QuoteState, string> = {
  open: "Vigente",
  converted: "Convertida",
  expired: "Vencida",
  cancelled: "Cancelada",
};

export interface StatusChip {
  label: string;
  tone: ChipTone;
}

/** The chips an order shows in lists: lifecycle first, then the two flags that need action. */
export function orderChips(o: Order, extra: { toValidate?: boolean } = {}): StatusChip[] {
  const chips: StatusChip[] = [];
  if (o.state === "cancelled") return [{ label: "Cancelada", tone: "neutral" }];
  if (o.has_pending_samples && o.state !== "closed") chips.push({ label: "Muestra pendiente", tone: "red" });
  if (extra.toValidate) chips.push({ label: "Por validar", tone: "amber" });
  else if (o.state === "in_process") chips.push({ label: "En proceso", tone: "blue" });
  else if (o.state === "registered" || o.state === "collected") {
    if (!o.has_pending_samples) chips.push({ label: o.state === "collected" ? "Muestra tomada" : "Registrada", tone: "neutral" });
  } else if (o.state === "reported_partial") chips.push({ label: "Validado parcial", tone: "green" });
  else if (o.state === "reported_final") chips.push({ label: "Validado", tone: "green" });
  else if (o.state === "closed") chips.push({ label: "Entregado", tone: "green" });
  if (o.balance_cents > 0) chips.push({ label: "Adeudo", tone: "amber" });
  return chips;
}

export function itemTone(s: OrderItemState): ChipTone {
  switch (s) {
    case "pending":
      return "neutral";
    case "captured":
      return "amber";
    case "validated":
    case "issued":
      return "green";
    case "amended":
      return "violet";
    case "cancelled":
      return "neutral";
  }
}

/**
 * One study's progress as Entrega shows it (Fernanda, 2026-11-02 20:29: "que se vea cuál es el que
 * ya se guardó y cuál ya está validado"). A `pending` study with values typed in is a partial capture.
 */
export function deliveryItemChip(it: Pick<OrderItem, "state" | "has_results">): StatusChip {
  switch (it.state) {
    case "pending":
      return it.has_results ? { label: "Captura incompleta", tone: "amber" } : { label: "Sin capturar", tone: "neutral" };
    case "captured":
      return { label: "Guardado, por validar", tone: "amber" };
    case "validated":
    case "issued":
      return { label: "Validado", tone: "green" };
    case "amended":
      return { label: "Corregido", tone: "violet" };
    case "cancelled":
      return { label: "Cancelado", tone: "neutral" };
  }
}

/**
 * Where an order stands in Entrega's list, for every non-cancelled order of the day (Fer, 2026-11-03 09:02:
 * "Nada por entregar" while the day's patients were still being captured). The category follows the
 * order's own state; the done/total counts only feed "En proceso n/m" and may be unknown until the
 * row's detail arrives.
 */
export type DeliveryStage = "none" | "in_process" | "ready" | "delivered";

export function deliveryStage(state: Order["state"]): DeliveryStage {
  switch (state) {
    case "closed":
      return "delivered";
    case "reported_final":
      return "ready";
    case "in_process":
    case "reported_partial":
      return "in_process";
    default:
      return "none";
  }
}

export function deliveryChip(state: Order["state"], progress?: { done: number; total: number }): StatusChip {
  switch (deliveryStage(state)) {
    case "delivered":
      return { label: "Entregado", tone: "green" };
    case "ready":
      return { label: "Listo para entregar", tone: "green" };
    case "in_process":
      return { label: progress ? `En proceso ${progress.done}/${progress.total}` : "En proceso", tone: "blue" };
    case "none":
      return { label: "Sin resultados", tone: "neutral" };
  }
}

/** Entrega's exception-first summary: how many studies are validated, and which are still missing, by state. */
export function studyProgress(items: Pick<OrderItem, "state" | "has_results" | "study_name">[]): { done: number; total: number; headline: string; missing: string } {
  const live = items.filter((i) => i.state !== "cancelled");
  const isDone = (i: Pick<OrderItem, "state">) => i.state === "validated" || i.state === "issued";
  const done = live.filter(isDone).length;
  const groups = new Map<string, string[]>();
  for (const it of live) if (!isDone(it)) {
    const label = deliveryItemChip(it).label;
    groups.set(label, [...(groups.get(label) ?? []), it.study_name]);
  }
  const headline = done === live.length ? (live.length === 1 ? "Validado" : `Los ${live.length} validados`) : `${done} de ${live.length} validados`;
  return { done, total: live.length, headline, missing: [...groups].map(([l, n]) => `${l}: ${n.join(", ")}`).join(" · ") };
}

export function quoteTone(s: QuoteState): ChipTone {
  return s === "open" ? "green" : s === "converted" ? "blue" : "neutral";
}

/**
 * Colour code computed on the device while typing, so the row reacts before the save. The server
 * computes the stored colour the same way (computeColour in packages/contracts): green inside the
 * interval, yellow (orange-brown on screen) anywhere outside it, high or low. There is no red: the
 * catalogue carries no critical limits, and a "times the limit" rule is not clinically defensible.
 * Text: yellow when it differs from the expected text.
 */
export function previewColour(args: { kind: string; value: string; low: number | null; high: number | null; expected: string | null }): ResultColour {
  const { kind, value, low, high, expected } = args;
  if (!value.trim()) return "none";
  const n = Number(value.replace(",", "."));
  // Numeric analytes, and text analytes that nonetheless carry a numeric interval (catalogue imports
  // leave some of those), are judged against the interval when the typed value is a number.
  if (kind === "numeric" || ((low !== null || high !== null) && Number.isFinite(n) && value.trim() !== "")) {
    if (!Number.isFinite(n)) return "none";
    if (low === null && high === null) return "green";
    return valueDirection({ value, low, high }) === null ? "green" : "yellow";
  }
  if (expected) return value.trim().toLowerCase() === expected.trim().toLowerCase() ? "green" : "yellow";
  return "none";
}

export type ResultDirection = "high" | "low";

/**
 * Whether a typed value is above ("high", ALTO) or below ("low", BAJO) the interval applied to this
 * patient (`low`/`high` already carry the sex/age/branch row). Null inside the interval, for an
 * empty or non-numeric value, and when there is no numeric bound (text answers, "Negativo", ...).
 * Same numeric rule as `previewColour`, so a flag always agrees with the row's colour.
 */
export function valueDirection(args: { value: string; low: number | null; high: number | null }): ResultDirection | null {
  const { value, low, high } = args;
  if (!value.trim() || (low === null && high === null)) return null;
  const n = Number(value.replace(",", "."));
  if (!Number.isFinite(n)) return null;
  if (low !== null && n < low) return "low";
  if (high !== null && n > high) return "high";
  return null;
}

export const DIRECTION_LABEL: Record<ResultDirection, string> = { high: "Alto", low: "Bajo" };
export const DIRECTION_ARROW: Record<ResultDirection, string> = { high: "↑", low: "↓" };

export const COLOUR_LABEL: Record<ResultColour, string> = {
  green: "Normal",
  yellow: "Alto o bajo",
  red: "Crítico: verificar",
  none: "Sin referencia",
};

/**
 * The "Muestras" card of an order with no tube. Since every analytic study gets a tube, an app
 * order without one carries only services; history migrated from the previous system has no
 * tube records at all, which is not the same as needing no sample.
 */
export function noSamplesText(origin: Order["origin"]): string {
  return origin === "migrated" ? "Solicitud del sistema anterior: sin registro de tubos." : "Esta solicitud no requiere toma de muestra.";
}
